"use client";

import { ArrowRight, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurLocation() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: mapRef,
    isVisible: mapVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const hours = [{
    label: "Monday – Thursday",
    value: "9 AM – 5 PM"
  }, {
    label: "Friday",
    value: "8:30 AM – 4 PM"
  }, {
    label: "Saturday – Sunday",
    value: "Closed"
  }];
  const mapEmbedUrl = "https://www.google.com/maps?q=1321+5th+Ave+%23202,+McKeesport,+PA+15132&output=embed";
  return <section data-section-id="4467" id="our-location" className="relative w-full bg-background text-foreground overflow-hidden">
      {}
      <div ref={introRef} className={`max-w-4xl mx-auto text-center px-6 pt-24 pb-16 transition-all duration-1000 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <p className="font-mono text-xs tracking-[0.15em] uppercase text-[var(--chart-2)] mb-6">LOCATION &amp; HOURS</p>
        <h2 className="font-default font-medium text-4xl md:text-6xl lg:text-7xl text-foreground leading-[1.1] tracking-tight">
          Office Location
        </h2>
        <div className="flex items-center justify-center mt-8">
          <Separator className="w-16 bg-[var(--chart-2)]/40" />
        </div>
        <p className="font-default text-base md:text-lg text-slate-text mt-8 max-w-2xl mx-auto leading-relaxed">
          Chaudhry Pulmonary Associates sees patients at 1321 5th Ave #202, McKeesport, PA 15132.
        </p>
      </div>

      {}
      <div ref={mapRef} className={`relative w-full transition-all duration-1000 ease-out ${mapVisible ? "opacity-100" : "opacity-0"}`}>
        <div className="relative w-full h-[60vh] min-h-[480px]">
          {}
          <iframe src={mapEmbedUrl} title="Chaudhry Pulmonary Associates Location" loading="lazy" className="absolute inset-0 w-full h-full border-0" style={{
          filter: "grayscale(0.2) contrast(1.1) brightness(0.9)"
        }} referrerPolicy="no-referrer-when-downgrade" />

          {}
          <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(135deg, hsla(222, 47%, 11%, 0.1) 0%, hsla(222, 47%, 11%, 0.05) 50%, hsla(222, 47%, 11%, 0.2) 100%)"
        }} />

          {}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative flex flex-col items-center -translate-y-12 md:-translate-y-16">
              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[80%] w-24 h-24 rounded-md">
                <div className="absolute inset-0 rounded-md animate-ping" style={{
                background: "radial-gradient(circle, hsla(174, 25%, 45%, 0.4) 0%, transparent 70%)"
              }} />
              </div>

              {}
              <div className="relative flex flex-col items-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-md flex items-center justify-center border-2 shadow-lg" style={{
                background: "linear-gradient(145deg, hsl(222, 47%, 11%) 0%, hsl(222, 47%, 20%) 100%)",
                borderColor: "hsl(174, 25%, 45%)"
              }}>
                  <span className="font-default font-bold text-3xl md:text-4xl text-white tracking-tighter">
                    CPA
                  </span>
                </div>
                {}
                <div className="w-1 h-8 -mt-1" style={{
                background: "linear-gradient(180deg, hsl(174, 25%, 45%) 0%, transparent 100%)"
              }} />
                <div className="w-3 h-3 rounded-md bg-[var(--chart-2)] -mt-2 shadow-md" />
              </div>
            </div>
          </div>

          {}
          <div className="absolute left-0 right-0 bottom-0 md:left-8 md:right-auto md:bottom-8 lg:left-16 lg:bottom-12 z-10 px-4 md:px-0">
            <div className="w-full md:w-[28rem] bg-card/95 backdrop-blur-md border border-slate-200 rounded-md shadow-xl p-8 md:p-10" style={{
            boxShadow: "0 20px 60px -10px hsla(222, 47%, 11%, 0.15), inset 0 1px 0 0 hsla(0, 0%, 100%, 0.5)"
          }}>
              <p className="font-mono text-[0.65rem] tracking-[0.15em] uppercase text-[var(--chart-2)] mb-5">
                Chaudhry Pulmonary Associates
              </p>

              <div className="flex items-start gap-3 mb-6">
                <MapPin className="w-5 h-5 mt-1 text-[var(--chart-2)] shrink-0" strokeWidth={1.5} />
                <address className="not-italic font-default text-lg md:text-xl text-card-foreground leading-snug">
                  1321 5th Ave #202
                  <br />
                  McKeesport, PA 15132
                  <br />
                  United States
                </address>
              </div>

              <Separator className="bg-border/60 mb-5" />

              <div className="mb-7">
                <p className="font-mono text-[0.65rem] tracking-[0.15em] uppercase text-slate-text mb-4">
                  Office Hours
                </p>
                <ul className="space-y-0">
                  {hours.map((item, i) => <li data-index={i} key={item.label} className="flex items-center justify-between py-3 border-b border-border/40 last:border-b-0">
                      <span className="font-default text-sm text-card-foreground/80">
                        {item.label}
                      </span>
                      <span className="font-default font-semibold text-sm text-[var(--chart-1)]">
                        {item.value}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild variant="outline" className="flex-1 h-11 bg-transparent border-slate-200 text-foreground hover:bg-secondary hover:text-foreground font-mono text-xs tracking-[0.1em] uppercase rounded-md transition-all">
                  <Link to="https://www.google.com/maps/dir/?api=1&destination=1321%205th%20Ave%20%23202%2C%20McKeesport%2C%20PA%2015132" newTab>
                    Directions
                    <ArrowRight className="w-3.5 h-3.5 ml-1" strokeWidth={1.5} />
                  </Link>
                </Button>
                <Button asChild className="flex-1 h-11 bg-[var(--chart-3)] text-[var(--primary)] hover:bg-[var(--chart-3)]/90 font-mono text-xs tracking-[0.1em] uppercase rounded-md shadow-md">
                  <Link to="/ContactPage">
                    Contact Us
                    <ArrowRight className="w-3.5 h-3.5 ml-1" strokeWidth={1.5} />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="h-0 md:h-0" />
      </div>

      {}
      <div className="h-24 md:h-32" />
    </section>;
}